import {useEffect} from 'react';

import {fuzzyMatchSanitized, matchPartsFromSegments} from 'common/util/fuzzy-match';

import {REPLAY_EVENT} from './query-utils';
import {
  EmbedReplayMessage,
  EmbedReplayPayload,
  isReplayWindowMessage,
  ParentAppMessagePayload,
  ParentAppReplayMessage,
  RecordingEventMetadata,
  ReplayMessage,
  TimeDisplaySetting,
  WindowMessageRecipient,
} from './types';

export function getRelevantReplayEvents(replayEvents: RecordingEventMetadata[]): RecordingEventMetadata[] {
  const existingEventMetadata = replayEvents || [];
  return existingEventMetadata.filter((event) => event.eventName !== REPLAY_EVENT);
}

export function getReplayEventCount(replayEvents: RecordingEventMetadata[]): number {
  return getRelevantReplayEvents(replayEvents).length;
}

export function formatRecordingProgressTimeBar(recordingProgress: number, recordingDuration: number) {
  let progress = new Date(recordingProgress);
  const duration = new Date(recordingDuration);

  if (progress > duration) {
    progress = duration;
  }

  let timeDisplaySetting = TimeDisplaySetting.Hours;
  if (duration.getUTCHours() === 0) {
    if (duration.getUTCMinutes() === 0) {
      timeDisplaySetting = TimeDisplaySetting.Seconds;
    } else {
      timeDisplaySetting = TimeDisplaySetting.Minutes;
    }
  }
  return {
    formattedProgress: formatRecordingProgressTime(progress, timeDisplaySetting),
    formattedDuration: formatRecordingProgressTime(duration, timeDisplaySetting),
  };
}

function formatRecordingProgressTime(date: Date, setting: TimeDisplaySetting) {
  if (setting === TimeDisplaySetting.Hours) {
    return date.toISOString().substring(11, 19);
  } else if (setting === TimeDisplaySetting.Minutes) {
    return date.toISOString().substring(14, 19);
  } else {
    return date.toISOString().substring(15, 19);
  }
}

export function getSearchHighlightPieces(
  inputText: string,
  searchFilter: string,
): Array<{text: string; highlight: boolean}> {
  if (!inputText) return [{text: inputText, highlight: false}];

  const match = fuzzyMatchSanitized(inputText.toLowerCase(), searchFilter.toLowerCase());
  if (searchFilter && match) {
    const matchParts = matchPartsFromSegments(inputText, match.segments);

    return matchParts.map((value, i) => {
      return {text: value, highlight: i % 2 === 0};
    });
  } else {
    return [{text: inputText, highlight: false}];
  }
}

export function sendEmbedReplayMessage(payload: EmbedReplayPayload, win: Window) {
  const message: EmbedReplayMessage = {payload, recipient: WindowMessageRecipient.ChildReplay};

  let origin;
  if (REPLAY_FROM_SAME_ORIGIN) {
    // for development environments, both embedded and parent origin should be the devbox
    origin = window.location.origin;
  } else {
    // otherwise, the origin is our CDN
    origin = `https://cdn.mxpnl.com`;
  }

  win.postMessage(message, origin);
}

// targetOrigin here is a variable since postMessage won't support multiple origins, and we need to be able to
// send messages to eu.mixpanel.com, staging-1.mixpanel.com etc.
// the EmbeddedReplay component won't be emitting anything senstive, but for best practice we'll let the parent app
// send down the current origin, and the child will check that against a whitelist and use that.
export function sendParentReplayMessage(payload: ParentAppMessagePayload, targetOrigin: string, win: Window) {
  const message: ParentAppReplayMessage = {payload, recipient: WindowMessageRecipient.ParentApp};

  let origin = targetOrigin;
  if (REPLAY_FROM_SAME_ORIGIN) {
    // for development environments, both embedded and parent origin should be the devbox
    origin = window.location.origin;
  }

  win.postMessage(message, origin);
}

export function useReplayWindowMessageListener<MessageType extends ReplayMessage>(
  recipient: MessageType['recipient'],
  win: Window,
  cb: (message: MessageType) => void,
) {
  useEffect(() => {
    function handleMessage(event: MessageEvent<MessageType>) {
      if (isReplayWindowMessage(event) && event.data.recipient === recipient) {
        cb(event.data);
      }
    }

    win.addEventListener(`message`, handleMessage);

    return () => {
      win.removeEventListener(`message`, handleMessage);
    };
  });
}
